import { createPortal } from "react-dom";
import { useEffect, useState, useContext } from "react";
import { css } from "../../../styled-system/css";
import { HStack, VStack } from "../../../styled-system/jsx";
import ConversationsSidebar from "./ConversationsSidebar";
import ChatHeader from "./ChatHeader";
import { ChatContext } from "./ChatContext";

const FullScreenChat = ({
  onStartNewConversation, // Function to start a new conversation
  currentConversation,
  setCurrentConversation,
  conversationData,
  client,
  setIsOpen,
  children, // Chat content (messages, input, etc.)
}) => {
  const { setIsFullScreen } = useContext(ChatContext);
  const [showConversations, setShowConversations] = useState(true);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsFullScreen(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [setIsFullScreen]);

  // Note: No body scroll lock needed since main-panel handles its own scrolling

  const conversation = conversationData?.data.results.find((conv) => conv.external_id === currentConversation);

  const fullScreenContent = (
    <div
      className={css({
        position: "fixed",
        top: 0, // Cover the header
        left: "80px", // Collapsed sidenav width
        right: 0,
        bottom: 0,
        backgroundColor: "#f8f9fa",
        zIndex: "overlay", // Below side navigation
        display: "flex",
        flexDirection: "column",
      })}
    >
      {/* Full screen chat container */}
      <HStack height="100%" width="100%" gap="0" alignItems="stretch">
        {/* Conversations Sidebar */}
        {showConversations && (
          <div
            className={css({
              width: "360px",
              flexShrink: 0,
              height: "100%",
              backgroundColor: "white",
              borderRight: "1px solid #ddd",
              position: "relative",
              zIndex: "auto",
              left: 0,
              top: 0,
            })}
          >
            <ConversationsSidebar
              onStartNewConversation={onStartNewConversation}
              currentConversation={currentConversation}
              setCurrentConversation={setCurrentConversation}
              conversationData={conversationData}
              client={client}
              isFullScreen={true}
            />
          </div>
        )}

        {/* Main Chat Area */}
        <VStack
          flexGrow={1}
          height="100%"
          width={"100%"}
          alignItems="stretch"
          gap="0"
          className={css({
            backgroundColor: "white",
          })}
        >
          {/* Chat Header */}
          <ChatHeader
            conversation={conversation}
            isFullScreen={true}
            onToggleFullScreen={() => setIsFullScreen(false)}
            onClose={() => {
              setIsFullScreen(false);
              setIsOpen(false); // Close the chat window completely
            }}
            onToggleConversations={() => setShowConversations(!showConversations)}
            showConversations={showConversations}
          />

          {/* Chat Content */}
          <VStack
            flexGrow={1}
            width="100%"
            height="100%"
            alignItems="stretch"
            gap="0"
            className={css({
              overflow: "hidden",
            })}
          >
            {children}
          </VStack>
        </VStack>
      </HStack>
    </div>
  );

  return createPortal(fullScreenContent, document.body);
};

export default FullScreenChat;
